import React from 'react';
import { useSettings } from '@/hooks/useSettings';
import AdminImageUpload from '@/components/feature/AdminImageUpload';

export interface MilestoneItem {
  year: string;
  title: string;
  desc: string;
}

export interface CathyStoryData {
  photoBadge: string;
  badge: string;
  title: string;
  titleHighlight: string;
  name: string;
  company: string;
  photoUrl: string;
  p1: string;
  p2: string;
  p3: string;
  p4: string;
  trajectoryTitle: string;
  milestones: MilestoneItem[];
}

interface CathyStoryProps {
  data: CathyStoryData;
  isEditing?: boolean;
  onChange?: (data: CathyStoryData) => void;
}

const CathyStory: React.FC<CathyStoryProps> = ({ data, isEditing = false, onChange }) => {
  const { settings } = useSettings();

  const handleMilestoneChange = (index: number, field: keyof MilestoneItem, value: string) => {
    if (!onChange) return;
    const updated = data.milestones.map((m, i) => (i === index ? { ...m, [field]: value } : m));
    onChange({ ...data, milestones: updated });
  };

  const handleAddMilestone = () => {
    if (!onChange) return;
    onChange({
      ...data,
      milestones: [...data.milestones, { year: '2025', title: 'Nuevo Hito', desc: 'Descripción del hito alcanzado.' }],
    });
  };

  const handleRemoveMilestone = (index: number) => {
    if (!onChange) return;
    onChange({
      ...data,
      milestones: data.milestones.filter((_, i) => i !== index),
    });
  };

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_100%_50%,rgba(140,82,255,0.1)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: Photo + Info */}
          <div className="w-full lg:w-5/12 lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#8c52ff]/20 to-[#00c2ff]/10 rounded-3xl blur-2xl scale-105" />
              <div className="relative bg-white/3 border border-white/10 rounded-3xl overflow-hidden">
                <img
                  src={data.photoUrl || "https://readdy.ai/api/search-image?query=confident%20latina%20entrepreneur%20businesswoman%20digital%20mentor%20professional%20photo%20studio%20purple%20blue%20lighting%20elegant%20modern%20background%20empowering&width=500&height=600&seq=cathy2&orientation=portrait"}
                  alt={`${data.name || ''} - ${data.photoBadge || ''}`}
                  className="w-full h-80 md:h-96 object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030006] via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={data.name || ''}
                        onChange={(e) => onChange && onChange({ ...data, name: e.target.value })}
                        className="w-full bg-black/60 border border-purple-500/50 rounded px-2 py-1 text-white font-bold text-lg focus:outline-none"
                        placeholder="Nombre completo"
                      />
                      <input
                        type="text"
                        value={data.photoBadge || ''}
                        onChange={(e) => onChange && onChange({ ...data, photoBadge: e.target.value })}
                        className="w-full bg-black/60 border border-purple-500/50 rounded px-2 py-1 text-[#8c52ff] text-xs font-semibold focus:outline-none"
                        placeholder="Cargo / Rol"
                      />
                      <input
                        type="text"
                        value={data.company || ''}
                        onChange={(e) => onChange && onChange({ ...data, company: e.target.value })}
                        className="w-full bg-black/60 border border-white/20 rounded px-2 py-1 text-white/50 text-xs focus:outline-none"
                        placeholder="Empresa"
                      />
                    </div>
                  ) : (
                    <>
                      <h2 className="text-white font-black text-2xl">{data.name}</h2>
                      <p className="text-[#8c52ff] text-sm font-semibold">{data.photoBadge}</p>
                      <p className="text-white/50 text-xs mt-1">{data.company}</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="mt-4">
                <AdminImageUpload
                  label="Foto de Perfil"
                  value={data.photoUrl || ''}
                  onChange={(url) => onChange && onChange({ ...data, photoUrl: url })}
                  folder="about"
                />
              </div>
            )}

            {/* Social Links */}
            <div className="mt-6 flex items-center gap-3 justify-center">
              {[
                { icon: 'ri-instagram-line', href: settings.instagramUrl, color: '#FF2DAA' },
                { icon: 'ri-facebook-circle-line', href: settings.facebookUrl, color: '#8c52ff' },
                { icon: 'ri-tiktok-line', href: settings.tiktokUrl, color: '#00c2ff' },
                { icon: 'ri-linkedin-box-line', href: settings.linkedinUrl, color: '#00c2ff' },
              ].filter(s => s.href).map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white transition-all duration-300 cursor-pointer"
                  style={{ '--hover-color': s.color } as React.CSSProperties}
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Right: Story */}
          <div className="flex-1 w-full relative z-10">
            {isEditing ? (
              <div className="space-y-4 mb-8">
                <div>
                  <label className="text-[11px] text-purple-400 uppercase tracking-wider block mb-1 font-semibold">Badge de Historia</label>
                  <input
                    type="text"
                    value={data.badge || ''}
                    onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
                    className="w-full bg-black/60 border border-purple-500/50 rounded-lg px-3 py-2 text-purple-300 text-xs font-bold focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                    placeholder="Mi Historia"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-white/60 uppercase tracking-wider block mb-1 font-semibold">Título</label>
                    <input
                      type="text"
                      value={data.title || ''}
                      onChange={(e) => onChange && onChange({ ...data, title: e.target.value })}
                      className="w-full bg-black/60 border border-purple-500/50 rounded-lg px-3 py-2 text-white font-bold text-base focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                      placeholder="De Emprendedora a"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-pink-400 uppercase tracking-wider block mb-1 font-semibold">Título Destacado</label>
                    <input
                      type="text"
                      value={data.titleHighlight || ''}
                      onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                      className="w-full bg-black/60 border border-pink-500/50 rounded-lg px-3 py-2 text-pink-300 font-bold text-base focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-400"
                      placeholder="Mentora Digital"
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <label className="text-[11px] text-white/60 uppercase tracking-wider block font-semibold">Párrafos de la Historia</label>
                  <div>
                    <span className="text-[10px] text-purple-300 block mb-0.5">Párrafo 1</span>
                    <textarea
                      value={data.p1 || ''}
                      onChange={(e) => onChange && onChange({ ...data, p1: e.target.value })}
                      rows={3}
                      placeholder="Párrafo 1..."
                      className="w-full bg-black/60 border border-purple-500/40 rounded-lg p-2.5 text-white text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-purple-300 block mb-0.5">Párrafo 2</span>
                    <textarea
                      value={data.p2 || ''}
                      onChange={(e) => onChange && onChange({ ...data, p2: e.target.value })}
                      rows={3}
                      placeholder="Párrafo 2..."
                      className="w-full bg-black/60 border border-purple-500/40 rounded-lg p-2.5 text-white text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-purple-300 block mb-0.5">Párrafo 3</span>
                    <textarea
                      value={data.p3 || ''}
                      onChange={(e) => onChange && onChange({ ...data, p3: e.target.value })}
                      rows={3}
                      placeholder="Párrafo 3..."
                      className="w-full bg-black/60 border border-purple-500/40 rounded-lg p-2.5 text-white text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-purple-300 block mb-0.5">Párrafo 4</span>
                    <textarea
                      value={data.p4 || ''}
                      onChange={(e) => onChange && onChange({ ...data, p4: e.target.value })}
                      rows={3}
                      placeholder="Párrafo 4..."
                      className="w-full bg-black/60 border border-purple-500/40 rounded-lg p-2.5 text-white text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest mb-4">{data.badge}</p>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight">
                  {data.title}
                  <span className="block bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] bg-clip-text text-transparent">
                    {data.titleHighlight}
                  </span>
                </h2>

                <div className="space-y-5 text-white/60 text-base leading-relaxed mb-12">
                  <p>{data.p1}</p>
                  <p>{data.p2}</p>
                  <p>{data.p3}</p>
                  <p>{data.p4}</p>
                </div>
              </>
            )}

            {/* Timeline */}
            <div className="space-y-6 pt-4">
              <div className="flex items-center justify-between mb-4">
                {isEditing ? (
                  <input
                    type="text"
                    value={data.trajectoryTitle || ''}
                    onChange={(e) => onChange && onChange({ ...data, trajectoryTitle: e.target.value })}
                    className="bg-black/40 border border-purple-500/40 rounded-lg px-3 py-1.5 text-white font-bold text-lg focus:outline-none"
                    placeholder="Título de Trayectoria"
                  />
                ) : (
                  <h3 className="text-white font-bold text-xl">{data.trajectoryTitle}</h3>
                )}

                {isEditing && (
                  <button
                    type="button"
                    onClick={handleAddMilestone}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 cursor-pointer"
                  >
                    <i className="ri-add-line" />
                    Añadir Hito
                  </button>
                )}
              </div>

              {data.milestones.map((m, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff] text-white text-xs font-black flex-shrink-0">
                      {i + 1}
                    </div>
                    {i < data.milestones.length - 1 && (
                      <div className="w-px flex-1 bg-gradient-to-b from-[#8c52ff]/40 to-transparent mt-2" />
                    )}
                  </div>
                  <div className="pb-6 flex-1 min-w-0">
                    {isEditing ? (
                      <div className="p-3 bg-white/5 border border-purple-500/20 rounded-xl space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={m.year || ''}
                            onChange={(e) => handleMilestoneChange(i, 'year', e.target.value)}
                            className="w-20 bg-black/40 border border-purple-500/40 rounded px-2 py-1 text-[#8c52ff] text-xs font-bold focus:outline-none"
                            placeholder="Año"
                          />
                          <input
                            type="text"
                            value={m.title || ''}
                            onChange={(e) => handleMilestoneChange(i, 'title', e.target.value)}
                            className="flex-1 bg-black/40 border border-purple-500/40 rounded px-2 py-1 text-white text-sm font-bold focus:outline-none"
                            placeholder="Título del hito"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveMilestone(i)}
                            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded cursor-pointer"
                            title="Eliminar Hito"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                        <textarea
                          value={m.desc || ''}
                          onChange={(e) => handleMilestoneChange(i, 'desc', e.target.value)}
                          rows={2}
                          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-white/70 text-xs focus:outline-none focus:border-purple-500"
                          placeholder="Descripción del hito"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-[#8c52ff] text-xs font-bold">{m.year}</span>
                          <h4 className="text-white font-bold text-base">{m.title}</h4>
                        </div>
                        <p className="text-white/50 text-sm leading-relaxed">{m.desc}</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CathyStory;
